import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, ArrowUpDown, ArrowUp, ArrowDown, Pencil, Trash2, X } from 'lucide-react';
import { sheltersApi } from '../api';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import type { SheltersListParams } from '../types';
import { citiesApi } from '@/features/cities/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function SheltersListPage() {
  const [params, setParams] = useState<SheltersListParams>({ page: 1, pageSize: 20 });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'id' | 'name' | 'contactEmail' | 'contactPhone' | 'address'>('id');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<any | null>(null);

  const queryClient = useQueryClient();
  const { isAdmin, isUser, isAdminWithShelter } = useAuth();
  const isSearching = !!(params.q && String(params.q).trim().length);
  const { data, isLoading } = useQuery({
    queryKey: ['shelters', params],
    queryFn: () =>
      isSearching
        ? sheltersApi.search({
            q: String(params.q),
            limit: params.pageSize || 20,
            skip: ((params.page || 1) - 1) * (params.pageSize || 20),
          })
        : sheltersApi.list(params),
  });

  // Cities lookup to display label even when search endpoint omits relation
  const { data: citiesAll } = useQuery({
    queryKey: ['cities-all-for-shelters-list'],
    queryFn: () => citiesApi.listFirst(),
  });
  const cityRows: any[] | undefined = Array.isArray(citiesAll) ? (citiesAll as any[]) : (citiesAll as any)?.data;
  const cityById = new Map<number, any>((cityRows || []).map((c: any) => [Number(c.id), c]));

  const { data: countData } = useQuery({
    queryKey: ['shelters-count', params.q],
    queryFn: () => sheltersApi.count(params.q ? { name: { like: params.q } } : undefined),
    enabled: !isSearching,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => sheltersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shelters'] });
      toast({ title: 'Shelter deleted' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'Failed to delete shelter' });
    },
  });

  const handleSearch = () => {
    setParams({ ...params, q: searchQuery, page: 1 });
  };

  const applySort = (column: typeof sortBy) => {
    const newDir = sortBy === column ? (sortDir === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
    setSortBy(column);
    setSortDir(newDir);
    setParams({
      ...params,
      filter: {
        ...(params.filter || {}),
        order: `${column} ${newDir}`,
      },
    });
  };

  const rows: any[] | undefined = Array.isArray(data) ? (data as any[]) : (data as any)?.data;

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Entidades</h1>
            <p className="text-muted-foreground">Gestiona las entidades</p>
          </div>
          {isAdmin && !isAdminWithShelter && (
            <Button asChild>
              <Link to="/shelters/new">
                <Plus className="mr-2 h-4 w-4" />
                Nueva Entidad
              </Link>
            </Button>
          )}
        </div>

        <Card className="p-6 mb-6">
          <div className="flex gap-4 items-center">
            {!isAdminWithShelter && (
              <>
                <div className="flex-1 relative">
                  <Input
                    placeholder="Buscar por nombre, email o teléfono..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pr-8"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      aria-label="Limpiar búsqueda"
                      title="Limpiar búsqueda"
                      className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setSearchQuery('');
                        setParams({ ...params, q: undefined, page: 1 });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Button onClick={handleSearch} disabled={!searchQuery.trim().length}>
                  <Search className="mr-2 h-4 w-4" />
                  Buscar
                </Button>
              </>
            )}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Por página</span>
              <Select
                value={(params.pageSize || 20).toString()}
                onValueChange={(val) =>
                  setParams({ ...params, pageSize: parseInt(val), page: 1 })
                }
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead onClick={() => applySort('id')} className="cursor-pointer select-none">
                  ID {sortBy === 'id' ? (
                    sortDir === 'ASC' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                  ) : (
                    <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  )}
                </TableHead>
                <TableHead onClick={() => applySort('name')} className="cursor-pointer select-none">
                  Nombre {sortBy === 'name' ? (
                    sortDir === 'ASC' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                  ) : (
                    <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  )}
                </TableHead>
                <TableHead onClick={() => applySort('contactEmail')} className="cursor-pointer select-none">
                  Email {sortBy === 'contactEmail' ? (
                    sortDir === 'ASC' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                  ) : (
                    <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  )}
                </TableHead>
                <TableHead onClick={() => applySort('contactPhone')} className="cursor-pointer select-none">
                  Teléfono {sortBy === 'contactPhone' ? (
                    sortDir === 'ASC' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                  ) : (
                    <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  )}
                </TableHead>
                <TableHead onClick={() => applySort('address')} className="cursor-pointer select-none">
                  Dirección {sortBy === 'address' ? (
                    sortDir === 'ASC' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                  ) : (
                    <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  )}
                </TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : (rows?.length || 0) === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No se encontraron entidades
                  </TableCell>
                </TableRow>
              ) : (
                rows?.map((shelter: any) => (
                  <TableRow key={shelter.id}>
                    <TableCell>{shelter.id}</TableCell>
                    <TableCell className="font-medium">{shelter.name}</TableCell>
                    <TableCell>{shelter.contactEmail || '-'}</TableCell>
                    <TableCell>{shelter.contactPhone || '-'}</TableCell>
                    <TableCell>{shelter.address || '-'}</TableCell>
                    <TableCell>{(() => {
                      const city = shelter.city || cityById.get(Number(shelter.cityId)) || cityById.get(Number((shelter as any).city_id));
                      return city ? `${city.name}${city.department ? ` - ${city.department}` : ''}` : '-';
                    })()}</TableCell>
                    <TableCell className="space-x-1">
                      {(isAdmin || isUser) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          aria-label="Editar"
                          title="Editar"
                        >
                          <Link to={`/shelters/${shelter.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Eliminar"
                          title="Eliminar"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setDeletingItem(shelter);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="p-4 border-t flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {isSearching ? (
                <>
                  Mostrando {(Array.isArray(data) ? data.length : data?.data?.length) || 0} resultados
                </>
              ) : (
                <>
                  Mostrando {(Array.isArray(data) ? data.length : data?.data?.length) || 0} de {countData?.count ?? '—'} resultados
                </>
              )}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={(params.page || 1) === 1}
                onClick={() => setParams({ ...params, page: (params.page || 1) - 1 })}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={
                  isSearching
                    ? ((Array.isArray(data) ? data.length : data?.data?.length) || 0) < (params.pageSize || 20)
                    : countData?.count !== undefined &&
                      (params.page || 1) * (params.pageSize || 20) >= (countData?.count || 0)
                }
                onClick={() => setParams({ ...params, page: (params.page || 1) + 1 })}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </Card>

        {/* Delete confirmation */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar entidad?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. Se eliminará permanentemente la entidad{' '}
                <strong>{deletingItem?.name}</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (deletingItem) {
                    deleteMutation.mutate(deletingItem.id);
                    setDeletingItem(null);
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
